import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const PublicObituaries = ({ config, onSelectObituary }) => {
  const [obituaries, setObituaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchObituaries();
  }, [page]);

  const fetchObituaries = async (searchQuery = search) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 12);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(`/api/public/obituaries?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        setObituaries(data.obituaries);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching obituaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchObituaries(search);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getFullName = (obituary) => {
    const parts = [];
    if (obituary.firstName) parts.push(obituary.firstName);
    if (obituary.middleName) parts.push(obituary.middleName);
    if (obituary.lastName) parts.push(obituary.lastName);
    if (obituary.suffix) parts.push(obituary.suffix);
    return parts.join(' ') || 'Unknown';
  };

  const getLifespan = (obituary) => {
    const birth = obituary.dateOfBirth ? new Date(obituary.dateOfBirth).getFullYear() : '?';
    const death = obituary.dateOfDeath ? new Date(obituary.dateOfDeath).getFullYear() : '?';
    return `${birth} - ${death}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900">
          {config?.obituaryPageTitle || 'In Memoriam'}
        </h2>
        {config?.obituaryPageDescription && (
          <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
            {config.obituaryPageDescription}
          </p>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="max-w-md mx-auto">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-brand-green-600 text-white text-sm rounded hover:bg-brand-green-700"
          >
            Search
          </button>
        </div>
      </form>

      {/* Results Count */}
      {!loading && (
        <p className="text-center text-sm text-gray-500">
          {total} memorial{total === 1 ? '' : 's'} found
        </p>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : obituaries.length === 0 ? (
        <div className="text-center py-12">
          <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No memorials found</p>
        </div>
      ) : (
        <>
          {/* Obituaries Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {obituaries.map((obituary) => (
              <button
                key={obituary.id}
                onClick={() => onSelectObituary(obituary.publicSlug)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow text-left"
              >
                {/* Photo or Placeholder */}
                <div className="aspect-[4/3] bg-gray-100 relative">
                  {obituary.attachments && obituary.attachments.length > 0 ? (
                    <img
                      src={`/api/public/obituaries/${obituary.publicSlug}/attachments/${obituary.attachments[0].id}`}
                      alt={getFullName(obituary)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <DocumentTextIcon className="h-16 w-16 text-gray-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getFullName(obituary)}
                  </h3>
                  {obituary.maidenName && (
                    <p className="text-sm text-gray-500">née {obituary.maidenName}</p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">
                    {getLifespan(obituary)}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PublicObituaries;
